/**
 * CanonicalUrl — global <link rel="canonical"> manager for the SPA.
 *
 * Mounted once inside <BrowserRouter> in App.js. On every route change,
 * updates the canonical <link> in the document head so Google, Bing and
 * other crawlers always know the definitive URL for the current page.
 *
 * Strategy
 * ========
 * - Default : canonical = `${origin}${pathname}` (strips ALL query strings
 *   and fragments). Prevents duplicate-content issues from tracking
 *   parameters (utm_*, fbclid, gclid) and permutations of filter combos.
 *
 * - Whitelist : a small allow-list of paths where specific filters DO
 *   matter enough to have their own canonical variant. Currently empty —
 *   the /properties filter combos are intentionally not indexed as
 *   distinct URLs (SEO best practice for e-commerce catalogs). If we ever
 *   want e.g. `/properties?city=Douala` to have its own canonical, add
 *   it to CANONICAL_ALLOWED_PARAMS below.
 *
 * Refs :
 *   - https://developers.google.com/search/docs/crawling-indexing/consolidate-duplicate-urls
 *   - https://developers.google.com/search/docs/appearance/site-names
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

// Paths → array of query param names allowed in the canonical. Everything
// else is stripped. Empty by default: filters do NOT create canonical
// variants (they'd fragment the PageRank across dozens of permutations).
const CANONICAL_ALLOWED_PARAMS = {
  // '/properties': ['city'],   // ← example : uncomment to let ?city=X be indexed
};

const buildCanonical = (origin, pathname, search) => {
  const allowed = CANONICAL_ALLOWED_PARAMS[pathname] || [];
  if (allowed.length === 0 || !search) {
    return `${origin}${pathname}`;
  }
  const src = new URLSearchParams(search);
  const kept = new URLSearchParams();
  // Preserve param order deterministically so canonical is stable.
  allowed.forEach((key) => {
    const val = src.get(key);
    if (val != null && val !== '') kept.set(key, val);
  });
  const q = kept.toString();
  return q ? `${origin}${pathname}?${q}` : `${origin}${pathname}`;
};

const CanonicalUrl = () => {
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const canonical = buildCanonical(window.location.origin, pathname, search);

    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonical);

    // Sync og:url too — the OG tag should agree with the canonical to avoid
    // conflicting signals to Facebook/LinkedIn. Only touch og:url when it
    // was previously set (index.html has a static default we should keep in
    // sync with the current route).
    const ogUrl = document.head.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', canonical);

    // We intentionally do NOT remove the tag on unmount — the next route
    // will overwrite the href, and if the app is fully unmounted the tag
    // becoming stale is benign.
    return undefined;
  }, [pathname, search]);

  return null;
};

export default CanonicalUrl;
